/**
 * AdventureBanner Component
 * 
 * Displays an adventure banner with WebP/JPEG fallback support and lazy loading.
 * Used in scene headers during gameplay alongside character portraits.
 * Aspect ratio: 5:1 (ultra-wide panoramic)
 */

import React, { useState, useEffect } from 'react';
import { imageService } from '../../services/imageService';
import type { ImageVariantSize, ImageUrls } from '../../services/imageService';
import { resolveAdventureBannerId } from '../../utils/adventureTitleImageId';
import useGameStore from '../../stores/gameStore';

export interface AdventureBannerProps {
  /**
   * Banner ID to display (e.g., 'fantasy', 'mystery', 'generic')
   * If undefined or not found, falls back to 'generic'
   */
  bannerId?: string | undefined;

  /**
   * Adventure ID for resolving unqualified banner IDs to adventure-specific
   * namespaced IDs (e.g. template_event → events/template/host:template_event).
   */
  adventureId?: string | undefined;
  
  /**
   * Size variant to display
   * - thumbnail: 320×64px (for scene headers)
   * - card: 640×128px (for medium displays)
   * - large: 1280×256px (for large displays)
   */
  variant?: ImageVariantSize;
  
  /**
   * Alt text for the image (for accessibility)
   */
  alt?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  
  /**
   * Custom fallback element to display if image fails to load
   */
  fallback?: React.ReactNode;
  
  /**
   * Whether to use lazy loading (default: true)
   */
  lazy?: boolean;

  /**
   * When true, the banner fills its container (width and height 100%).
   * Use in a fixed-height row (e.g. scene header) so banner matches portrait height;
   * sides may be trimmed via object-fit cover.
   */
  fillContainer?: boolean;
}

/**
 * Default fallback component when banner cannot be loaded
 */
const DefaultFallback: React.FC<{ variant: ImageVariantSize; alt: string | undefined }> = ({ variant, alt }) => {
  const sizes = {
    thumbnail: 'w-80 h-16',     // 320×64 equivalent (5:1)
    card: 'w-[40rem] h-32',      // 640×128 equivalent (5:1)
    large: 'w-[80rem] h-64',     // 1280×256 equivalent (5:1)
  };

  return (
    <div 
      className={`${sizes[variant]} flex items-center justify-center bg-gray-700 text-gray-400 overflow-hidden`}
      style={{
        borderRadius: '12px',
        border: '1px solid #000000',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)'
      }}
      role="img"
      aria-label={alt || 'Adventure banner placeholder'}
    >
      <span className="text-2xl">━━━━━━━━━━━</span>
    </div>
  );
};

/**
 * AdventureBanner component
 */
export const AdventureBanner: React.FC<AdventureBannerProps> = ({
  bannerId,
  adventureId,
  variant = 'thumbnail',
  alt,
  className = '',
  style,
  fallback,
  lazy = true,
  fillContainer = false,
}) => {
  const sessionAdventureId = useGameStore(
    (state) =>
      state.currentAdventure?.id
      ?? state.selectedAdventureId
      ?? state.currentSession?.adventureId
      ?? undefined
  );
  const effectiveAdventureId = adventureId ?? sessionAdventureId;

  const [resolvedBannerId, setResolvedBannerId] = useState<string | undefined>(undefined);
  const [resolveReady, setResolveReady] = useState(!effectiveAdventureId);
  // Track which URL failed (not just a boolean)
  const [isLoading, setIsLoading] = useState(true);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  useEffect(() => {
    let cancelled = false;

    const resolveId = async () => {
      if (!effectiveAdventureId) {
        if (!cancelled) {
          setResolvedBannerId(bannerId);
          setResolveReady(true);
        }
        return;
      }

      if (!cancelled) {
        setResolveReady(false);
      }

      try {
        await imageService.loadAvailableIds();
      } catch {
        // Resolver falls back to header value when IDs are unavailable
      }

      if (!cancelled) {
        setResolvedBannerId(
          resolveAdventureBannerId(
            effectiveAdventureId,
            bannerId,
            imageService.getBannerIds()
          )
        );
        setResolveReady(true);
      }
    };

    void resolveId();

    return () => {
      cancelled = true;
    };
  }, [effectiveAdventureId, bannerId]);

  const activeBannerId =
    resolveReady && resolvedBannerId !== undefined ? resolvedBannerId : undefined;

  const appendRetryParam = (url: string, count: number): string => {
    if (count <= 0) {
      return url;
    }
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}retry=${count}`;
  };

  const urls: ImageUrls = imageService.getBannerUrls(activeBannerId ?? 'generic', variant);
  const imageUrls: ImageUrls = {
    webp: appendRetryParam(urls.webp, retryCount),
    jpg: appendRetryParam(urls.jpg, retryCount),
  };

  useEffect(() => {
    if (!activeBannerId) {
      return;
    }
    setFailedUrl(null);
    setRetryCount(0);
    setIsLoading(true);
  }, [activeBannerId, variant]);

  if (!activeBannerId) {
    const sizeClasses = {
      thumbnail: 'w-80 h-16',
      card: 'w-[40rem] h-32',
      large: 'w-[80rem] h-64',
    };
    const containerSizeClass = fillContainer ? 'w-full h-full' : sizeClasses[variant];
    const fillStyle = fillContainer ? { width: '100%', height: '100%', minHeight: 0 } : undefined;

    return (
      <div
        className={`${containerSizeClass} relative ${className}`}
        style={{
          borderRadius: '12px',
          border: '1px solid #000000',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          backgroundColor: 'transparent',
          ...fillStyle,
          ...style,
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center bg-gray-700 animate-pulse">
          <span className="text-gray-500 text-xs">Loading...</span>
        </div>
      </div>
    );
  }

  // If THIS specific URL failed, show fallback
  if (failedUrl === imageUrls.jpg) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <DefaultFallback variant={variant} alt={alt} />
    );
  }

  // Size classes for the container (5:1 aspect ratio) when not filling a parent
  const sizeClasses = {
    thumbnail: 'w-80 h-16',     // 320×64 equivalent
    card: 'w-[40rem] h-32',      // 640×128 equivalent
    large: 'w-[80rem] h-64',     // 1280×256 equivalent
  };
  const containerSizeClass = fillContainer ? 'w-full h-full' : sizeClasses[variant];
  const fillStyle = fillContainer ? { width: '100%', height: '100%', minHeight: 0 } : undefined;

  // Handle image load error
  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const failedSrc = e.currentTarget.src;
    console.warn(`Failed to load banner: ${resolvedBannerId}`);
    if (retryCount < maxRetries) {
      setTimeout(() => {
        setRetryCount((current) => current + 1);
        setIsLoading(true);
      }, 300 * (retryCount + 1));
      return;
    }
    setFailedUrl(failedSrc);
    setIsLoading(false);
  };

  // Handle image load success
  const handleLoad = () => {
    setIsLoading(false);
  };

  // Use URLs as key to force remount when they change
  const imageKey = `${imageUrls.webp}-${imageUrls.jpg}`;

  return (
    <div 
      className={`${containerSizeClass} relative ${className}`}
      style={{
        borderRadius: '12px',
        border: '1px solid #000000',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        ...fillStyle,
        ...style
      }}
    >
      {/* Show placeholder while loading */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-700 animate-pulse">
          <span className="text-gray-500 text-xs">Loading...</span>
        </div>
      )}
      
      {/* Picture element with WebP and JPEG sources */}
      {/* Using key prop to force remount when URLs change, clearing error state */}
      <picture key={imageKey} style={{ display: 'block', width: '100%', height: '100%' }}>
        {/* WebP source (modern browsers) */}
        <source
          type="image/webp"
          srcSet={imageUrls.webp}
        />
        
        {/* JPEG fallback (all browsers) */}
        <img
          src={imageUrls.jpg}
          alt={alt || 'Adventure banner'}
          loading={lazy ? 'lazy' : 'eager'}
          onError={handleError}
          onLoad={handleLoad}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
          className={`${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        />
      </picture>
    </div>
  );
};

export default AdventureBanner;

